import { useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { Button, Modal } from '~/components';
import { Form } from 'semantic-ui-react';
import CreateNewProjectModal from '~/modals/CreateNewProject';
import CreatePortfolioModal from '~/modals/CreatePortfolioModal';
import { useCreateFromDocumentProgress } from '~/features/createFromDocument/CreateFromDocumentProgressProvider';
import { useUserContext } from '~/hooks';
import { getInstallers } from '~/api/admin';
import { MAX_FILE_SIZE_BYTES } from '~/api/createFromDocument';
import { useQuery } from 'react-query';
import { DeploymentTypeDisplay, DeploymentTypes } from '~/constants/deployment-type';
import { CustomerTypes, ProjectInterconnectionTypeCodes, RecOwnership, RevenueSource } from '~/constants';
import { Project } from '~/types/project';

const ACCEPTED_LABEL = 'PDF, XLSX, or XLS (max 10MB)';

type FlowType = 'choice' | 'manual';

export default function CreateDealModal({
  closeModal,
}: {
  closeModal: () => void;
}) {
  const { loggedInUser } = useUserContext();
  const [flow, setFlow] = useState<FlowType>('choice');
  const [installerId, setInstallerId] = useState<string>('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [manualModalType, setManualModalType] = useState<'project' | 'portfolio' | null>(null);
  const [projectData, setProjectData] = useState<Partial<Project>>({});

  const { data: installers = [] } = useQuery({
    queryKey: ['admin', 'installers'],
    queryFn: () => getInstallers(),
    enabled: Boolean(loggedInUser?.isAdmin),
  });

  const installerOptions = useMemo(() => {
    return (installers as { id: string; company?: { name?: string } }[])
      .map((inst) => ({
        key: inst.id,
        value: inst.id,
        text: inst.company?.name ?? `Installer ${inst.id}`,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  }, [installers]);

  const progress = useCreateFromDocumentProgress();

  const { mutateAsync: runUploadAndStart, status: uploadStatus } = useMutation(
    async (file: File) => {
      if (!progress) throw new Error('Progress provider unavailable.');
      await progress.startIngestion({
        file,
        installerId,
        additionalContext: additionalContext.trim() || undefined,
      });
    },
    {
      onSuccess: () => {
        setSelectedFile(null);
        setAdditionalContext('');
        closeModal();
      },
      onError: (err: any) => {
        const message =
          err?.response?.data?.message ?? err?.message ?? 'Upload or start failed.';
        toast.error(message);
      },
    }
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE_BYTES,
    accept: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ],
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (!installerId) return;
      if (rejectedFiles?.length) {
        const sizeReject = rejectedFiles.find((f) => (f.errors as any)?.some((e: any) => e.code === 'file-too-large'));
        toast.error(
          sizeReject
            ? 'File must be 10MB or smaller.'
            : `Please upload a single file of type ${ACCEPTED_LABEL}.`
        );
        return;
      }
      if (acceptedFiles?.length === 1) {
        setSelectedFile(acceptedFiles[0]);
      }
    },
    disabled: !installerId || uploadStatus === 'loading',
  });

  const handleSubmitImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first.');
      return;
    }
    await runUploadAndStart(selectedFile);
  };

  const isAdmin = loggedInUser?.isAdmin;
  const isInstallerMissingForAdmin = Boolean(isAdmin && !installerId);

  const selectProjectType = (deploymentType: keyof typeof DeploymentTypes) => {
    const data: Partial<Project> = {
      deploymentType,
      interconnectionType: ProjectInterconnectionTypeCodes.FRONT_OF_METER,
      ...(isAdmin && installerId ? { installerId } : {}),
    };
    switch (deploymentType) {
      case DeploymentTypes.CI:
        data.interconnectionType = ProjectInterconnectionTypeCodes.BEHIND_METER;
        break;
      case DeploymentTypes.COMMUNITY_SOLAR:
        data.revenueSource = RevenueSource.COMMUNITY_SOLAR_SUBS;
        data.recOwnership = RecOwnership.INVESTOR;
        data.customerType = CustomerTypes.COMMUNITY_SOLAR;
        break;
    }
    setProjectData(data);
    setManualModalType('project');
  };

  if (manualModalType === 'project') {
    return (
      <CreateNewProjectModal
        closeModal={() => {
          setManualModalType(null);
          setFlow('choice');
          closeModal();
        }}
        projectData={projectData}
          installerId={isAdmin ? installerId : undefined}
      />
    );
  }

  if (manualModalType === 'portfolio') {
    return (
      <CreatePortfolioModal
        closeModal={() => {
          setManualModalType(null);
          setFlow('choice');
          closeModal();
        }}
          installerId={isAdmin ? installerId : undefined}
      />
    );
  }

  return (
    <Modal closeModal={closeModal}>
      <Modal.Title>Create your deal</Modal.Title>
      <Modal.Content>
        <Form>
          {isAdmin && (
            <Form.Field>
              <label>Select installer (required)</label>
              <Form.Select
                placeholder="Search or select installer"
                options={installerOptions}
                value={installerId}
                onChange={(_, { value }) => setInstallerId(String(value ?? ''))}
                search
                selection
                fluid
                style={{ marginBottom: installerId ? 'var(--medium)' : 0 }}
              />
            </Form.Field>
          )}

          {isAdmin && flow === 'choice' && (
            <>
              {installerId && (
                <>
                  <div
                    {...getRootProps()}
                    style={{
                      padding: 'var(--x-large)',
                      marginBottom: 'var(--medium)',
                      minHeight: 140,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `2px dashed ${isDragActive ? 'var(--color-primary)' : '#ddd'}`,
                      borderRadius: 8,
                      background: isDragActive ? 'var(--color-primary-light, #e3f2fd)' : 'var(--color-neutral-50, #fafafa)',
                      textAlign: 'center',
                      cursor: installerId ? 'pointer' : 'not-allowed',
                      opacity: installerId ? 1 : 0.6,
                      transition: 'border-color 0.2s, background 0.2s',
                    }}
                  >
                    <input {...getInputProps()} />
                    <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: 'var(--color-neutral-800)' }}>
                      {isDragActive ? 'Drop your file here' : 'Drop a document here or click to upload'}
                    </p>
                    <p style={{ margin: 'var(--x-small) 0 0', fontSize: '0.9rem', color: 'var(--color-neutral-600)' }}>
                      {ACCEPTED_LABEL}
                    </p>
                  </div>
                  {selectedFile && (
                    <div style={{ margin: '-20px 0 var(--medium)', display: 'block' }}>
                      <span style={{ color: 'var(--color-neutral-700)', fontSize: '0.9rem' }}>
                        Selected: {selectedFile.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                          color: 'var(--color-primary)',
                          textDecoration: 'underline',
                          fontWeight: 500,
                          cursor: 'pointer',
                          marginLeft: '8px',
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  <Form.Field>
                    <label>Additional AI context (optional)</label>
                    <Form.TextArea
                      placeholder="Add missing data points, nuanced details, or instructions to guide AI parsing."
                      value={additionalContext}
                      onChange={(_, { value }) => setAdditionalContext(String(value ?? ''))}
                      rows={4}
                    />
                  </Form.Field>
                  <div style={{ marginTop: 'var(--small)', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Button
                      primary
                      disabled={!selectedFile || uploadStatus === 'loading'}
                      loading={uploadStatus === 'loading'}
                      onClick={() => void handleSubmitImport()}
                      style={{ width: '100%' }}
                    >
                      Submit
                    </Button>
                  </div>
                  <p style={{ marginTop: 'var(--medium)', marginBottom: 0, textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => installerId && setFlow('manual')}
                      disabled={!installerId}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        color: 'var(--color-primary)',
                        textDecoration: 'underline',
                        cursor: installerId ? 'pointer' : 'not-allowed',
                        opacity: installerId ? 1 : 0.6,
                      }}
                    >
                      Enter data manually
                    </button>
                  </p>
                </>
              )}
            </>
          )}
          {(flow === 'manual' || !isAdmin) && (
            <Form.Field>
              <label>Create a project</label>
              {isInstallerMissingForAdmin && (
                <p style={{ marginTop: 'var(--xx-small)', marginBottom: 'var(--x-small)', color: '#db2828' }}>
                  Please select an installer before creating a project or portfolio.
                </p>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--x-small)', marginTop: 'var(--xx-small)' }}>
                <Button
                  size="small"
                  primary
                  outlined
                  disabled={isInstallerMissingForAdmin}
                  onClick={() => {
                    if (isInstallerMissingForAdmin) return;
                    selectProjectType(DeploymentTypes.CI);
                  }}
                >
                  {DeploymentTypeDisplay.CI}
                </Button>
                <Button
                  size="small"
                  primary
                  outlined
                  disabled={isInstallerMissingForAdmin}
                  onClick={() => {
                    if (isInstallerMissingForAdmin) return;
                    selectProjectType(DeploymentTypes.COMMUNITY_SOLAR);
                  }}
                >
                  {DeploymentTypeDisplay.COMMUNITY_SOLAR}
                </Button>
                <Button
                  size="small"
                  primary
                  outlined
                  disabled={isInstallerMissingForAdmin}
                  onClick={() => {
                    if (isInstallerMissingForAdmin) return;
                    selectProjectType(DeploymentTypes.UTILITY);
                  }}
                >
                  {DeploymentTypeDisplay.UTILITY}
                </Button>
              </div>
              <label style={{ marginTop: 'var(--small)' }}>Create a portfolio from existing projects</label>
              <Button
                primary
                outlined
                size="small"
                disabled={isInstallerMissingForAdmin}
                onClick={() => {
                  if (isInstallerMissingForAdmin) return;
                  setManualModalType('portfolio');
                }}
                style={{ marginTop: 'var(--xx-small)' }}
              >
                Create portfolio
              </Button>
              {isAdmin && (
                <div style={{ marginTop: 'var(--small)' }}>
                  <p style={{ marginTop: 'var(--medium)', marginBottom: 0, textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => setFlow('choice')}
                      disabled={!installerId}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        color: 'var(--color-primary)',
                        textDecoration: 'underline',
                        cursor: installerId ? 'pointer' : 'not-allowed',
                        opacity: installerId ? 1 : 0.6,
                      }}
                    >
                      Upload a document
                    </button>
                  </p>

                </div>
              )}
            </Form.Field>
          )}
        </Form>
      </Modal.Content>
    </Modal>
  );
}
